import { Fragment } from "react";
import { Paper, Table, TableBody, TableCell, TableContainer, TableRow } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { Delete } from "@mui/icons-material";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { UpdateWorkOrderInputSchema } from "DDD/action-objects/WorkOrderItemsUpdate";
import EquipmentPieceOptions from "modules/equipmentPieces/EquipmentPieceOptions";
import ScrollAsyncAutocomplete from "components/ScrollAsyncAutocomplete/ScrollAsyncAutocomplete";
import AddEquipmentCost from "modules/proposals/equipmentCosts/AddEquipmentCost";
import { JobPhase } from "generated/graphql";
import { denseTableSx, getEquipmentRowDetails, headerCellSx } from "./utils";

export default function EquipmentTable({ jobPhase }: { jobPhase: JobPhase }) {
  const { control, getValues } = useFormContext<UpdateWorkOrderInputSchema>();
  const {
    fields: equipmentPieceFields,
    remove: removeEquipment,
    append: equipmentPieceAppend,
  } = useFieldArray({
    control,
    name: "equipmentPieces",
  });

  return (
    <MDBox mb={3} sx={{ "@media print": { marginBottom: 1 } }}>
      <TableContainer component={Paper} sx={{ overflowX: "visible" }}>
        <Table aria-label="equipment" sx={denseTableSx}>
          <TableBody>
            <TableRow>
              <TableCell variant="head" sx={headerCellSx}>
                Equipment
              </TableCell>
              <TableCell variant="head" align="right" width="72px" sx={headerCellSx}>
                Qty
              </TableCell>
              <TableCell variant="head" align="right" width="72px" sx={headerCellSx}>
                Hours
              </TableCell>
              <TableCell
                variant="head"
                align="center"
                width="40px"
                className="no-print"
                sx={headerCellSx}
              >
                <AddEquipmentCost
                  compactTrigger
                  submitMsg="Add Equipment"
                  isJobCosting
                  onAdd={(equipmentIds) => {
                    equipmentIds?.forEach((ep) => {
                      equipmentPieceAppend({
                        id: typeof ep === "string" || typeof ep === "number" ? ep : ep?.id,
                        name: ep?.name,
                      });
                    });
                  }}
                />
              </TableCell>
            </TableRow>
            {equipmentPieceFields?.map((equipment, index) => {
              const equipmentRow = getEquipmentRowDetails(
                jobPhase,
                getValues(`equipmentPieces.${index}.id`)
              );

              return (
                <Fragment key={equipment.id}>
                  <TableRow>
                    <TableCell
                      sx={{
                        display: "none",
                        "@media print": {
                          display: "table-cell",
                        },
                      }}
                    >
                      {getValues(`equipmentPieces.${index}.name`)}
                    </TableCell>
                    <TableCell className="no-print">
                      <EquipmentPieceOptions>
                        {({ equipmentPieceOptions, loading, search, pagination }) => (
                          <Controller
                            name={`equipmentPieces.${index}.id` as const}
                            control={control}
                            render={({ field }) => (
                              <ScrollAsyncAutocomplete
                                {...field}
                                value={{
                                  label:
                                    equipmentPieceOptions.find((x) => x.value === field.value)
                                      ?.label ??
                                    getValues(`equipmentPieces.${index}.name`) ??
                                    "",
                                  value: field.value,
                                }}
                                options={equipmentPieceOptions}
                                loading={loading}
                                label=""
                                search={search}
                                onLoadMore={pagination.loadMore}
                                hasMore={pagination.hasNextPage}
                                textFieldProps={{
                                  size: "small",
                                  sx: {
                                    "& .MuiOutlinedInput-input": {
                                      padding: "8.5px 12px",
                                    },
                                    "& .MuiOutlinedInput-root": {
                                      minHeight: "unset",
                                    },
                                    "& .MuiFormLabel-root": {
                                      display: "none",
                                    },
                                  },
                                }}
                              />
                            )}
                          />
                        )}
                      </EquipmentPieceOptions>
                    </TableCell>
                    <TableCell align="right">{equipmentRow.quantity}</TableCell>
                    <TableCell align="right">{equipmentRow.hours}</TableCell>
                    <TableCell align="right" className="no-print">
                      <MDButton
                        variant="text"
                        color="error"
                        sx={{ minHeight: 0, p: 0 }}
                        onClick={() => removeEquipment(index)}
                      >
                        <Delete />
                      </MDButton>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </MDBox>
  );
}
